/**
 * Service Overview 탭 전용 — 이 서비스가 어떤 팩으로 구성됐는지 한눈에 보이는 요약 패널.
 *
 * 구조
 * - 헤더: "Ontology packs" + "Edit packs" 버튼 (클릭 시 onEditPacks → ServiceDetailPage 가 Ontology 탭으로 전환)
 * - 스타터 매치 배지: slice.packIds 셋이 어느 preset.packIds 셋과 정확히 일치하면 "Matches starter: …" 노출
 * - PII 경고 배너: sales 팩이 포함된 경우 서비스 격리 권장 문구
 * - 칩 리스트: packIds 각 id 에 대해 카탈로그에서 icon + displayName 을 조회해 렌더
 * - 블럭 드릴다운: "Built from N blocks" 토글 버튼 클릭 → 블럭 카드 2열 그리드 expand
 *   - 각 카드: icon + displayName + kinds 개수
 *   - 카드 클릭 → `/blocks#{id}` 로 이동 (블럭 페이지의 해당 블럭 앵커로 스크롤)
 *
 * 데이터 로드
 * - `getOntologyPacks()` + `getOntologyPresets()` + `getOntologyBlocks()` 를 mount 시 병렬 호출
 * - best-effort — 실패하면 "카탈로그 로드 실패" 문구 없이 id 만으로 렌더 (패널 자체는 빈 상태로라도 동작)
 */
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import type {
	AdminClient,
	OntologyBlock,
	OntologyPack,
	OntologyPreset,
	OntologySlice
} from "../../api/admin-client.ts"
import { uniqueBlockIds } from "./pack-block-utils.ts"

interface OntologyPacksPanelProps {
	readonly slice: OntologySlice
	readonly client: AdminClient
	/** "Edit packs" 버튼 클릭 시 호출. 보통 ServiceDetailPage 가 Ontology 탭으로 이동시킨다. */
	readonly onEditPacks: () => void
}

/** sales 팩이 포함되면 서비스 격리 권장 문구를 띄우기 위한 id. */
const SALES_PACK_ID = "sales"

/**
 * 두 id 셋이 **완전 동일** 한지 비교 — starter preset 매치 판정 기준.
 *
 * @param a 슬라이스의 packIds
 * @param b preset 의 packIds
 * @returns 정확히 같은 id 집합이면 true
 */
function sameIdSet(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
	if (a.length !== b.length) return false
	const set = new Set(a)
	for (const id of b) {
		if (!set.has(id)) return false
	}
	return true
}

/** 슬라이스 요약 카드 한 칸 — 숫자 + 라벨. 클릭 없음, 정보 전용. */
function SliceTotal(props: { readonly value: number; readonly label: string }) {
	return (
		<div className="ontology-packs-total">
			<span className="ontology-packs-total-v">{props.value}</span>
			<span className="ontology-packs-total-k">{props.label}</span>
		</div>
	)
}

export function OntologyPacksPanel(props: OntologyPacksPanelProps) {
	const [packs, setPacks] = useState<ReadonlyArray<OntologyPack>>([])
	const [presets, setPresets] = useState<ReadonlyArray<OntologyPreset>>([])
	const [blocks, setBlocks] = useState<ReadonlyArray<OntologyBlock>>([])
	const [expanded, setExpanded] = useState(false)

	useEffect(() => {
		let cancelled = false
		void (async () => {
			try {
				const [packList, presetList, blockList] = await Promise.all([
					props.client.getOntologyPacks(),
					props.client.getOntologyPresets(),
					props.client.getOntologyBlocks()
				])
				if (!cancelled) {
					setPacks(packList)
					setPresets(presetList)
					setBlocks(blockList)
				}
			} catch {
				// best-effort — 카탈로그 없으면 id 만 렌더 (아래 fallback 로직이 처리)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [props.client])

	const packIds = props.slice.packIds ?? []
	const packById = useMemo(() => new Map(packs.map((p) => [p.id, p])), [packs])
	const blockById = useMemo(() => new Map(blocks.map((b) => [b.id, b])), [blocks])
	// packIds 는 렌더마다 새 배열 참조가 아닌 slice prop 에서 파생 — 의도적으로 join 비교
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional stable key via join
	const matchedStarter = useMemo(
		() => presets.find((p) => sameIdSet(packIds, p.packIds)),
		[presets, packIds.join(",")]
	)
	const hasSales = packIds.includes(SALES_PACK_ID)

	// 선택된 팩들이 조합하는 고유 블럭 id 합집합 — pack-block-utils 로 계산.
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional stable key via join
	const blockIds = useMemo(() => {
		const selectedPacks = packIds.flatMap((id) => {
			const p = packById.get(id)
			return p ? [p] : []
		})
		return uniqueBlockIds(selectedPacks)
	}, [packIds.join(","), packById])

	return (
		<div className="panel">
			<div className="panel-head">
				<h3>온톨로지 팩</h3>
				<button type="button" className="btn btn-ghost btn-sm" onClick={props.onEditPacks}>
					팩 편집
				</button>
			</div>

			{packIds.length === 0 ? (
				<p className="muted" style={{ fontSize: 13 }}>
					사용자 커스텀 슬라이스 — 팩 선택 내역 없음
				</p>
			) : (
				<>
					{matchedStarter && (
						<div style={{ marginBottom: 10 }}>
							<span className="badge" title="스타터 프리셋과 완전 일치">
								스타터 일치: {matchedStarter.displayName}
							</span>
						</div>
					)}

					{hasSales && (
						<div className="alert alert-warn" style={{ marginBottom: 12, fontSize: 12.5 }}>
							sales 팩이 포함된 슬라이스는 고객 PII 를 담을 수 있습니다. 서비스 격리가 필요한지 재검토하세요.
						</div>
					)}

					<div className="ontology-packs-totals" aria-label="슬라이스 요약">
						<SliceTotal value={packIds.length} label="팩" />
						<SliceTotal value={blockIds.length} label="블럭" />
						<SliceTotal value={props.slice.kinds.length} label="종류" />
						<SliceTotal value={props.slice.domains.length} label="도메인" />
						<SliceTotal value={props.slice.relations.length} label="관계" />
						<SliceTotal value={props.slice.shapes.length} label="형태" />
					</div>

					<ul className="ontology-packs-cards" aria-label="팩 구성">
						{packIds.map((id) => {
							const pack = packById.get(id)
							if (!pack) {
								return (
									<li key={id} className="ontology-packs-card is-fallback">
										<code className="inline">{id}</code>
										<span className="muted">카탈로그 로드 대기 중</span>
									</li>
								)
							}
							return (
								<li key={id} className="ontology-packs-card">
									<header className="ontology-packs-card-head">
										<span aria-hidden className="ontology-packs-card-icon">
											{pack.icon}
										</span>
										<div className="ontology-packs-card-title">
											<strong>{pack.displayName}</strong>
											<code className="inline">{pack.id}</code>
										</div>
										<span className="badge ontology-packs-card-group" title={`group: ${pack.group}`}>
											{pack.group}
										</span>
									</header>
									{pack.description && <p className="ontology-packs-card-desc">{pack.description}</p>}
									<div className="ontology-packs-card-stats">
										<span>blocks {pack.blockIds.length}</span>
										<span>kinds {pack.slice.kinds.length}</span>
										<span>relations {pack.slice.relations.length}</span>
										<span>shapes {pack.slice.shapes.length}</span>
									</div>
								</li>
							)
						})}
					</ul>

					{blockIds.length > 0 && (
						<div className="ontology-packs-expandable">
							<button
								type="button"
								className="ontology-packs-toggle"
								aria-expanded={expanded}
								onClick={() => setExpanded((v) => !v)}
							>
								<span aria-hidden className="ontology-packs-toggle-caret">
									{expanded ? "▾" : "▸"}
								</span>
								{blockIds.length}개 블럭으로 구성
							</button>

							{expanded && (
								<ul className="ontology-packs-block-grid" aria-label="슬라이스 구성 블럭">
									{blockIds.map((id) => {
										const block = blockById.get(id)
										// 블럭 카탈로그가 늦게 로드되면 id 만 칩으로 노출 — 항상 안전하게 렌더.
										if (!block) {
											return (
												<li key={id} className="ontology-packs-block-item">
													<div className="ontology-packs-block-card is-fallback">
														<code className="inline">{id}</code>
													</div>
												</li>
											)
										}
										return (
											<li key={id} className="ontology-packs-block-item">
												<Link
													to={`/blocks#${id}`}
													className="ontology-packs-block-card"
													title={block.description}
												>
													<div className="ontology-packs-block-card-head">
														<span aria-hidden className="ontology-packs-block-card-icon">
															{block.icon}
														</span>
														<strong>{block.displayName}</strong>
													</div>
													<span className="ontology-packs-block-card-meta">
														kinds {block.slice.kinds.length}
													</span>
												</Link>
											</li>
										)
									})}
								</ul>
							)}
						</div>
					)}
				</>
			)}
		</div>
	)
}
